import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Phone } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { logo, brand, waLink } from "@/content/site";
import { Arrow, Ltr } from "@/components/apex/primitives";

const Header = () => {
  const { t, isRtl, toggle, lang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const links = [
    { href: "/designs", label: t.nav.designs },
    { href: "/#visualizer", label: t.nav.visualizer },
    { href: "/#features", label: t.nav.features },
    { href: "/#about", label: t.nav.about },
    { href: "/#faq", label: t.nav.faq },
    { href: "/#contact", label: t.nav.contact },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const onDark = !scrolled && !open;

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-[rgba(247,247,240,0.92)] shadow-[0_10px_40px_-20px_rgba(21,21,21,0.25)] backdrop-blur-xl" : "bg-transparent"
        }`}
      >
        <div
          className={`mx-auto flex max-w-[1400px] items-center gap-6 px-5 transition-all duration-500 sm:px-8 ${
            scrolled ? "h-[72px]" : "h-[92px]"
          }`}
        >
          <a href="/" data-testid="header-logo-link" className="shrink-0" aria-label={brand.name_en}>
            <img
              src={logo.src}
              alt={isRtl ? logo.alt_ar : logo.alt_en}
              width="150"
              height="60"
              className={`h-9 w-auto brightness-0 transition-all duration-500 sm:h-11 ${onDark ? "invert" : ""}`}
            />
          </a>

          <nav aria-label={t.nav.home} className="mx-auto hidden items-center gap-1 lg:flex">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.href.replace("/", "").replace("#", "")}`}
                className={`rounded-full px-4 py-2 text-[14.5px] font-medium transition-colors duration-300 ${
                  onDark ? "text-white/85 hover:text-[var(--yellow)]" : "text-[var(--text)] hover:text-[var(--yellow-dk)]"
                }`}
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="ms-auto flex items-center gap-3 lg:ms-0">
            <button
              type="button"
              onClick={toggle}
              data-testid="language-switch"
              aria-label={t.langSwitchAria}
              className={`ap-mono h-10 rounded-full px-4 text-[12px] font-bold tracking-[0.15em] transition-colors duration-300 ${
                onDark ? "bg-white/15 text-white hover:bg-[var(--yellow)] hover:text-[var(--charcoal)]" : "bg-white text-[var(--text)] hover:bg-[var(--yellow)]"
              }`}
            >
              {t.langLabel}
            </button>
            <a
              href={waLink(t.float.defaultMsg)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-cta-whatsapp"
              className="ap-btn hidden !h-12 !px-6 !text-[14px] sm:inline-flex"
            >
              {t.nav.cta}
              <Arrow />
            </a>

            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-expanded={open}
              aria-controls="apex-mobile-menu"
              aria-label={open ? t.nav.menuClose : t.nav.menuOpen}
              className={`relative grid h-11 w-11 place-items-center rounded-full transition-colors duration-300 lg:hidden ${
                onDark ? "bg-white/15 text-white" : "bg-white text-[var(--text)]"
              }`}
            >
              <span className="relative block h-3 w-5">
                <motion.span
                  className="absolute inset-x-0 block h-[2px] rounded-full bg-current"
                  animate={open ? { top: 5, rotate: 45 } : { top: 0, rotate: 0 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                />
                <motion.span
                  className="absolute inset-x-0 block h-[2px] rounded-full bg-current"
                  animate={open ? { top: 5, rotate: -45 } : { top: 10, rotate: 0 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                />
              </span>
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            id="apex-mobile-menu"
            key={lang}
            data-testid="mobile-menu-panel"
            role="dialog"
            aria-modal="true"
            aria-label={t.nav.menuOpen}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 flex flex-col bg-[var(--cream)] px-5 pb-8 pt-28 lg:hidden"
          >
            <nav className="flex flex-col gap-2">
              {links.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.href.replace("/", "").replace("#", "")}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="ap-display flex items-center justify-between rounded-2xl bg-white px-6 py-4 text-xl text-[var(--text)] transition-colors active:bg-[var(--yellow)]"
                >
                  {l.label}
                  <Arrow />
                </motion.a>
              ))}
            </nav>
            <div className="mt-auto space-y-3 pt-8">
              <a
                href={waLink(t.float.defaultMsg)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-whatsapp"
                className="ap-btn w-full"
              >
                {t.nav.cta}
                <Arrow />
              </a>
              <a href={brand.telUrl} data-testid="mobile-menu-call" className="ap-btn-dark w-full">
                <Phone className="h-4 w-4" strokeWidth={2} />
                <Ltr className="ap-mono">{brand.phoneDisplay}</Ltr>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
